import PointerInteraction from 'ol/interaction/Pointer';
import MapBrowserEventType from 'ol/MapBrowserEventType';
import EventType from 'ol/events/EventType';
import Point from 'ol/geom/Point';
import Feature from 'ol/Feature';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import LineString from 'ol/geom/LineString';
import { Circle, Fill, Stroke } from 'ol/style';
import { fromString as colorFromString } from 'ol/color';
import GeoJSON from 'ol/format/GeoJSON';
import h from 'hyperscript';
import clickedMarker from '@disy/cadenza-icons/clicked-location.svg';

import { ProgressSpinner } from 'ui/progress-spinner/progress-spinner';

import { fitMapView } from 'cadenza/map/map-utils';
import { MapFlyoutHandler } from 'cadenza/map/map-flyout-handler';
import { COLORS } from 'cadenza/utils/colors/colors';
import { CustomAppMessageType, LocationSelectedOrigin } from 'cadenza/map/custom/custom-app-message-type';
import { parseSvg } from 'cadenza/utils/svg';
import { logger } from 'cadenza/utils/logging';
import { findAddress } from 'cadenza/api-client/geocoding-api';

import i18n from './interactions.properties';
import './address-info-interaction-flyout.css';

const primaryColor = colorFromString(window.Disy.theme['primary-color']);

const flyoutStyle = 'address-info-interaction-flyout';
const markerYOffset = 8; // half of the actual svg icon size

let markerStyle;
export const MARKER_STYLE_PROVIDER = () => {
  if (!markerStyle) {
    const svg = parseSvg(clickedMarker);
    svg.querySelector('path:first-child').style.fill = window.Disy.theme['primary-color'];
    markerStyle = new Style({
      image: new Icon({
        src: 'data:image/svg+xml;base64,' + btoa(svg.outerHTML),
        size: [ 16, 16 ],
        imgSize: [ 16, 16 ],
        anchor: [ 0.5, 0.5 ]
      })
    });
  }
  return markerStyle;
};

export class AddressInfoInteraction extends PointerInteraction {

  #map;
  #vectorSource;
  #markerLayer;
  #mapFlyoutHandler;

  constructor (map) {
    super({ handleEvent: event => this._handleEvent(event) });
    this.#map = map;
    this.#mapFlyoutHandler = new MapFlyoutHandler(map, { closeOnOutsideClick: true, closeOnInsideClick: true });
    this._createAndAddMarkerLayer();

    this.on('change:active', () => {
      if (!this.getActive()) {
        this.#vectorSource.refresh();
      }
    });
  }

  setActive (active) {
    super.setActive(active);
    if (!active) {
      this._cleanUpUI();
    }
  }

  _cleanUpUI () {
    this.#mapFlyoutHandler.clear();
    this.#mapFlyoutHandler.closeFlyout();
    this.#vectorSource.refresh();
  }

  _createAndAddMarkerLayer () {
    this.#vectorSource = new VectorSource();
    this.#markerLayer = new VectorLayer({
      id: 'reverseGeocoding',
      source: this.#vectorSource,
      map: this.#map
    });
  }

  _handleEvent (mapBrowserEvent) {
    if (mapBrowserEvent.type === MapBrowserEventType.SINGLECLICK
        || mapBrowserEvent.type === MapBrowserEventType.DBLCLICK) {
      const clickedPoint = new Point(mapBrowserEvent.coordinate);
      const srsName = this.#map.getView().getProjection().getCode();

      this._drawLocationMarker(clickedPoint);
      this._displayLoadingFlyout(clickedPoint.getCoordinates());

      findAddress(clickedPoint, srsName).then(address => {
        this.#vectorSource.refresh();
        this._drawClickMarker(clickedPoint);
        if (address) {
          this._dispatchLocationSelected(address);
          this._displayAddress(clickedPoint, mapBrowserEvent, address);
        } else {
          this._dispatchLocationSelected({ point: { coordinates: clickedPoint.getCoordinates() }, srsCode: srsName });
          this._displayNotFoundFlyout(clickedPoint.getCoordinates());
        }
      }).catch(error => logger.error('Cannot obtain address information', error));
    } else if (mapBrowserEvent.type === EventType.WHEEL
        || mapBrowserEvent.type === MapBrowserEventType.POINTERDOWN) {
      this.#mapFlyoutHandler.closeFlyout();
    }

    if (mapBrowserEvent.type === MapBrowserEventType.DBLCLICK) {
      return false; // prevent default zoom
    } else {
      return super.handleEvent(mapBrowserEvent);
    }
  }

  _dispatchLocationSelected ({ point, srsCode, address, geocoderId } = {}) {
    const data = { coordinate: point.coordinates, srsCode, address, searchProviderId: geocoderId, origin: LocationSelectedOrigin.POINTER };
    this.#map.getTargetElement().dispatchEvent(new CustomEvent(CustomAppMessageType.OUTGOING.LOCATION_SELECTED, {
      detail: data,
      bubbles: true
    }));
  }

  _displayAddress (clickedPoint, mapBrowserEvent, { point, formattedAddressLines } = {}) {
    const addressPoint = new GeoJSON().readGeometry(point);
    this._drawLocationMarker(addressPoint);
    this._drawLine(clickedPoint, addressPoint);
    if (mapBrowserEvent.type === MapBrowserEventType.DBLCLICK) {
      fitMapView(this.#map, addressPoint).then(() => {
        // without the timeout the flyout position would be a little off sometimes
        setTimeout(() => this._displayAddressFlyout(addressPoint.getCoordinates(), formattedAddressLines), 20);
      }).catch(error => logger.error('Cannot zoom to address', error));
    } else {
      this._displayAddressFlyout(addressPoint.getCoordinates(), formattedAddressLines);
    }
  }

  _drawLine (point, addressPoint) {
    const clickedLineEnd = point.getCoordinates();
    const addressLineEnd = addressPoint.getCoordinates();
    const line = new Feature(new LineString([ clickedLineEnd, addressLineEnd ]));
    line.setStyle(new Style({
      stroke: new Stroke({
        color: primaryColor,
        width: 2,
        lineDash: [ 3, 6 ]
      })
    }));
    this.#vectorSource.addFeature(line);
  }

  _drawClickMarker (clickPoint) {
    const clickFeature = new Feature(clickPoint);
    const fill = new Fill({
      color: primaryColor
    });
    const stroke = new Stroke({
      color: COLORS.WHITE_09,
      width: 1.25
    });
    clickFeature.setStyle(new Style({
      image: new Circle({
        fill,
        stroke,
        radius: 5
      }),
      fill,
      stroke
    }));
    this.#vectorSource.addFeature(clickFeature);
  }

  _drawLocationMarker (addressPoint) {
    const markerFeature = new Feature(addressPoint);
    markerFeature.setStyle(MARKER_STYLE_PROVIDER());
    this.#vectorSource.addFeature(markerFeature);
  }

  _displayAddressFlyout (coordinates, formattedAddressLines) {
    const addressLineHtml = h(
      'p',
      formattedAddressLines
        .flatMap(addressLine => addressLine.trim() ? [ h('br'), document.createTextNode(addressLine) ] : [])
        .slice(1)
    );

    this.#mapFlyoutHandler.displayFlyout(coordinates, addressLineHtml, markerYOffset, flyoutStyle);
  }

  _displayLoadingFlyout (coordinates) {
    this.#mapFlyoutHandler.displayFlyout(coordinates, new ProgressSpinner({ label: i18n('addressInfo.addressIsLoading'), size: 'xs' }), markerYOffset, flyoutStyle);
  }

  _displayNotFoundFlyout (coordinates) {
    this.#mapFlyoutHandler.displayFlyout(coordinates, i18n('addressInfo.noAddressFound'), markerYOffset, flyoutStyle);
  }

}
